/**
 * Content Manager - Microsoft Azure AD Configuration
 *
 * Configures MSAL (Microsoft Authentication Library) for Azure AD authentication.
 * This enables users to sign in with their Microsoft/Azure AD accounts.
 *
 * Prerequisites:
 * 1. Register an app in Azure Portal (App registrations)
 * 2. Configure redirect URIs (e.g., http://localhost:3000/auth/azure/callback)
 * 3. Copy the Application (client) ID and Directory (tenant) ID
 * 4. Set the environment variables
 */

import { Configuration, LogLevel, PublicClientApplication } from '@azure/msal-browser';

// =============================================================================
// CONFIGURATION
// =============================================================================

/**
 * Azure AD configuration from environment variables
 */
export const azureConfig = {
  clientId: process.env.REACT_APP_AZURE_CLIENT_ID || '',
  tenantId: process.env.REACT_APP_AZURE_TENANT_ID || '',
  redirectUri: process.env.REACT_APP_AZURE_REDIRECT_URI || `${window.location.origin}/auth/azure/callback`,
};

/**
 * Check if Azure AD is configured
 */
export function isAzureConfigured(): boolean {
  return Boolean(azureConfig.clientId && azureConfig.tenantId);
}

/**
 * MSAL configuration object
 */
export const msalConfig: Configuration = {
  auth: {
    clientId: azureConfig.clientId,
    // Use tenant-specific authority for single-tenant apps
    // Use 'common' for multi-tenant apps
    authority: azureConfig.tenantId
      ? `https://login.microsoftonline.com/${azureConfig.tenantId}`
      : 'https://login.microsoftonline.com/common',
    redirectUri: azureConfig.redirectUri,
    postLogoutRedirectUri: window.location.origin,
    // IMPORTANT: Set to false to prevent MSAL from auto-navigating back to the login page
    // before our callback handler can process and persist the authentication result.
    // With an explicit callback page (/auth/azure/callback), we handle the redirect
    // ourselves and navigate to the app after successful authentication.
    navigateToLoginRequestUrl: false,
  },
  cache: {
    cacheLocation: 'localStorage',
    storeAuthStateInCookie: false, // Set to true for IE11 support
  },
  system: {
    loggerOptions: {
      loggerCallback: (level, message, containsPii) => {
        if (containsPii) {
          return;
        }
        switch (level) {
          case LogLevel.Error:
            console.error('[MSAL]', message);
            return;
          case LogLevel.Warning:
            console.warn('[MSAL]', message);
            return;
          case LogLevel.Info:
            // Only log in development
            if (process.env.NODE_ENV === 'development') {
              console.info('[MSAL]', message);
            }
            return;
          case LogLevel.Verbose:
            // Only log in development with verbose enabled
            if (process.env.NODE_ENV === 'development' && process.env.REACT_APP_MSAL_VERBOSE === 'true') {
              console.debug('[MSAL]', message);
            }
            return;
        }
      },
      logLevel: process.env.NODE_ENV === 'development' ? LogLevel.Warning : LogLevel.Error,
    },
  },
};

// =============================================================================
// SCOPES
// =============================================================================

/**
 * Scopes for user authentication
 * - openid: Required for OIDC
 * - profile: User profile info (name, etc.)
 * - email: User email address
 * - User.Read: Read signed-in user's basic profile from Microsoft Graph
 */
export const loginScopes = {
  scopes: ['openid', 'profile', 'email', 'User.Read'],
};

/**
 * Scopes for reading all users from Azure AD (admin consent required)
 * Used for syncing users from Azure AD to the app
 * Note: Using full Microsoft Graph URL format for reliable token acquisition
 * with delegated permissions
 *
 * Includes:
 * - User.Read.All: Read all users' profiles
 * - GroupMember.Read.All: Read group memberships (for group-based filtering)
 */
export const userSyncScopes = {
  scopes: [
    'https://graph.microsoft.com/User.Read.All',
    'https://graph.microsoft.com/GroupMember.Read.All',
  ],
};

// =============================================================================
// MSAL INSTANCE
// =============================================================================

let msalInstance: PublicClientApplication | null = null;

/**
 * Get or create the MSAL instance
 * Uses lazy initialization to avoid errors when Azure AD is not configured
 */
export function getMsalInstance(): PublicClientApplication | null {
  if (!isAzureConfigured()) {
    console.warn('[MSAL] Azure AD not configured. Set REACT_APP_AZURE_CLIENT_ID and REACT_APP_AZURE_TENANT_ID.');
    return null;
  }

  if (!msalInstance) {
    msalInstance = new PublicClientApplication(msalConfig);
  }

  return msalInstance;
}

/**
 * Initialize the MSAL instance
 * Call this during app startup to prepare MSAL for use
 * Note: Does NOT call handleRedirectPromise() - that should be done
 * explicitly by the callback page to capture the auth result
 */
export async function initializeMsal(): Promise<PublicClientApplication | null> {
  const instance = getMsalInstance();
  if (!instance) {
    return null;
  }

  try {
    // Initialize MSAL instance (required before any operations)
    await instance.initialize();
    return instance;
  } catch (error) {
    console.error('[MSAL] Initialization error:', error);
    return null;
  }
}

const msalExports = {
  azureConfig,
  msalConfig,
  loginScopes,
  userSyncScopes,
  isAzureConfigured,
  getMsalInstance,
  initializeMsal,
};

export default msalExports;
